import React from 'react';

/**
 * UserAvatar - A reusable avatar component that displays profile photos or initials
 * 
 * @param {string} name - User's name for generating initials
 * @param {string} imageUrl - Optional profile image URL
 * @param {string} size - Size variant: 'sm' (32px), 'md' (40px), 'lg' (48px), 'xl' (56px)
 * @param {string} className - Additional CSS classes
 */
export const UserAvatar = ({ 
  name = '', 
  imageUrl = null, 
  size = 'md',
  className = '' 
}) => {
  // Generate initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  };

  // Size configurations
  const sizeConfig = {
    sm: { container: 'w-8 h-8', text: 'text-xs' },      // 32px
    md: { container: 'w-10 h-10', text: 'text-sm' },    // 40px
    lg: { container: 'w-12 h-12', text: 'text-base' },  // 48px
    xl: { container: 'w-14 h-14', text: 'text-lg' },    // 56px
  };

  const config = sizeConfig[size] || sizeConfig.md;
  const initials = getInitials(name);

  // Generate a consistent background color based on name
  const getBackgroundColor = (name) => {
    const colors = [
      'bg-red-100 text-red-700',
      'bg-blue-100 text-blue-700',
      'bg-green-100 text-green-700',
      'bg-purple-100 text-purple-700',
      'bg-orange-100 text-orange-700',
      'bg-pink-100 text-pink-700',
      'bg-teal-100 text-teal-700',
      'bg-indigo-100 text-indigo-700',
    ];
    
    if (!name) return colors[0];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  if (imageUrl) {
    return (
      <div 
        className={`${config.container} rounded-full overflow-hidden flex-shrink-0 ${className}`}
      >
        <img 
          src={imageUrl} 
          alt={name}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Hide broken image and show initials fallback
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <div 
          className={`${config.container} ${getBackgroundColor(name)} rounded-full items-center justify-center font-semibold hidden`}
          style={{ display: 'none' }}
        >
          <span className={config.text}>{initials}</span>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`${config.container} ${getBackgroundColor(name)} rounded-full flex items-center justify-center font-semibold flex-shrink-0 ${className}`}
    >
      <span className={config.text}>{initials}</span>
    </div>
  );
};

export default UserAvatar;
